import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { query, type CircuitEscalatDto, type UsuariDto } from './activacions';
import { ApiError, apiRequest, apiRequestAmbEtag } from './client';
import type { Pagina, TipusArea } from './types';

export type Periodicitat = 'SETMANAL' | 'QUINZENAL' | 'MENSUAL';
export type EstatSollicitud = 'PENDENT' | 'APROVADA' | 'REBUTJADA' | 'CANCELLADA';
export type TipusSollicitud = 'CANVI' | 'SUBSTITUCIO' | 'INTERCANVI';
export type AccioHistorial = 'CREAR' | 'MODIFICAR' | 'ELIMINAR' | 'ROTACIO' | 'CANVI_APROVAT';
export type EstatFranja = 'COBERTA' | 'SENSE_COBERTURA';

export interface CalendariDto {
  id: number;
  nom: string;
  departamentId?: number | null;
  areaId?: number | null;
  fusHorari?: string;
  recordatoriMinuts?: number;
  actiu: boolean;
  version?: number;
}

export interface TornDto {
  id?: number | null;
  calendariId: number;
  titularId: number;
  substitutId?: number | null;
  inici: string;
  fi: string;
  origen?: string | null;
  rotacioId?: number | null;
  notes?: string | null;
  version?: number;
}

export interface RotacioDto {
  id?: number | null;
  calendariId: number;
  periodicitat: Periodicitat;
  diaInici: number;
  horaCanvi: string;
  participants: number[];
  desde: string;
  fins?: string | null;
  actiu: boolean;
  version?: number;
}

export interface ResultatRotacioDto {
  rotacio: RotacioDto;
  previsualitzacio: boolean;
  torns: TornDto[];
  /** Solapaments detectats pel servidor (nombre o llista de torns afectats), si n'informa. */
  conflictes?: number | TornDto[] | null;
}

export interface FranjaCoberturaDto {
  inici: string;
  fi: string;
  estat: EstatFranja;
  tornId?: number | null;
  titularId?: number | null;
}

export interface CoberturaDto {
  calendariId: number;
  desde: string;
  percentatge: number;
  dies: { data: string; percentatge: number; franges: FranjaCoberturaDto[] }[];
  forats: FranjaCoberturaDto[];
}

export interface HistorialDto {
  id: number;
  calendariId: number;
  tornId?: number | null;
  accio: AccioHistorial;
  usuariId?: number | null;
  abans?: Partial<TornDto> | null;
  despres?: Partial<TornDto> | null;
  at: string;
}

export interface SollicitudCanviDto {
  id: number;
  tornId: number;
  tornIntercanviId?: number | null;
  sollicitantId: number;
  tipus: TipusSollicitud;
  proposatId?: number | null;
  nouInici?: string | null;
  nouFi?: string | null;
  motiu?: string | null;
  estat: EstatSollicitud;
  resoltPer?: number | null;
  resoltAt?: string | null;
  comentariResolucio?: string | null;
  createdAt: string;
  version?: number;
}

export interface DepartamentDto {
  id: number;
  codi?: string;
  nom: string;
}

export interface AreaDto {
  id: number;
  departamentId: number;
  codi?: string;
  nom: string;
  tipus: TipusArea;
}

export interface ServeiDto {
  id: number;
  areaId: number;
  codi?: string;
  nom: string;
}

export interface ExpertServeiDto {
  id?: number | null;
  serveiId: number;
  usuariId: number;
  nom?: string | null;
  tipus: 'TITULAR' | 'SUBSTITUT';
  ordre: number;
  especialitat?: string | null;
}

export const clausPlan = {
  calendaris: ['calendaris'] as const,
  calendari: (id: number) => ['calendaris', id] as const,
  torns: (id: number, desde: string, fins: string) => ['calendaris', id, 'torns', desde, fins],
  rotacions: (id: number) => ['calendaris', id, 'rotacions'] as const,
  cobertura: (id: number, desde: string, dies: number) => [
    'calendaris',
    id,
    'cobertura',
    desde,
    dies,
  ],
  historial: (id: number) => ['calendaris', id, 'historial'] as const,
  sollicituds: ['sollicituds-canvi'] as const,
};

export function useCalendaris() {
  return useQuery({
    queryKey: clausPlan.calendaris,
    queryFn: () => apiRequest<CalendariDto[]>('/calendaris'),
  });
}

export function useCalendari(id: number) {
  return useQuery({
    queryKey: clausPlan.calendari(id),
    queryFn: () => apiRequest<CalendariDto>(`/calendaris/${id}`),
    enabled: Number.isFinite(id),
  });
}

export function useTorns(id: number, desde: string, fins: string) {
  return useQuery({
    queryKey: clausPlan.torns(id, desde, fins),
    queryFn: () => apiRequest<TornDto[]>(`/calendaris/${id}/torns${query({ desde, fins })}`),
    enabled: Number.isFinite(id),
  });
}

export function useRotacions(id: number) {
  return useQuery({
    queryKey: clausPlan.rotacions(id),
    queryFn: () => apiRequest<RotacioDto[]>(`/calendaris/${id}/rotacions`),
    enabled: Number.isFinite(id),
  });
}

const cobertura = (id: number, desde: string, dies: number) =>
  apiRequest<CoberturaDto>(`/calendaris/${id}/cobertura${query({ desde, dies })}`);

export function useCobertura(id: number | null, desde: string, dies: number) {
  return useQuery({
    queryKey: clausPlan.cobertura(id ?? -1, desde, dies),
    queryFn: () => cobertura(id as number, desde, dies),
    enabled: id != null && Number.isFinite(id),
  });
}

export function useCoberturesCalendaris(ids: number[], desde: string, dies: number) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: clausPlan.cobertura(id, desde, dies),
      queryFn: () => cobertura(id, desde, dies),
    })),
  });
}

export function useRotacionsCalendaris(ids: number[]) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: clausPlan.rotacions(id),
      queryFn: () => apiRequest<RotacioDto[]>(`/calendaris/${id}/rotacions`),
    })),
  });
}

export function useHistorialCalendaris(ids: number[]) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: clausPlan.historial(id),
      queryFn: () => apiRequest<HistorialDto[]>(`/calendaris/${id}/historial`),
    })),
  });
}

export function useSollicituds(estat: EstatSollicitud | '', enabled = true) {
  return useQuery({
    queryKey: [...clausPlan.sollicituds, estat],
    queryFn: () => apiRequest<SollicitudCanviDto[]>(`/sollicituds-canvi${query({ estat })}`),
    enabled,
  });
}

export function useTorn(id: number | null | undefined) {
  return useQuery({
    queryKey: ['torns', id],
    queryFn: () => apiRequest<TornDto>(`/torns/${id}`),
    enabled: id != null,
    retry: false,
  });
}

export function useDepartaments() {
  return useQuery({
    queryKey: ['departaments'],
    queryFn: () => apiRequest<DepartamentDto[]>('/departaments'),
    staleTime: 300_000,
  });
}

export function useArees() {
  return useQuery({
    queryKey: ['arees'],
    queryFn: () => apiRequest<AreaDto[]>('/arees'),
    staleTime: 300_000,
  });
}

export function useServeis() {
  return useQuery({
    queryKey: ['serveis'],
    queryFn: () => apiRequest<ServeiDto[]>('/serveis'),
    staleTime: 300_000,
  });
}

export function useExpertsServeis(ids: number[]) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ['serveis', id, 'experts'],
      queryFn: () => apiRequest<ExpertServeiDto[]>(`/serveis/${id}/experts`),
    })),
  });
}

export function useCircuitsServeis(ids: number[]) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ['circuit', id],
      queryFn: async () => {
        try {
          return await apiRequest<CircuitEscalatDto>(`/serveis/${id}/circuit-escalat`);
        } catch (e) {
          if (e instanceof ApiError && (e.status === 404 || e.status === 403)) return null;
          throw e;
        }
      },
      retry: false,
    })),
  });
}

export function useUsuaris() {
  return useQuery({
    queryKey: ['usuaris', 'directori'],
    queryFn: async () => {
      try {
        return (await apiRequest<Pagina<UsuariDto>>(`/usuaris${query({ size: 500 })}`)).items;
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) return [];
        throw e;
      }
    },
    staleTime: 300_000,
    retry: false,
  });
}

export function nomUsuari(u: UsuariDto): string {
  return `${u.nom} ${u.cognoms ?? ''}`.trim();
}

export function useNoms() {
  const usuaris = useUsuaris();
  const mapa = new Map<number, string>();
  for (const u of usuaris.data ?? []) mapa.set(u.id, nomUsuari(u));
  return (id: number | null | undefined): string => {
    if (id == null) return '';
    return mapa.get(id) ?? `#${id}`;
  };
}

function useInvalidaCalendari() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: clausPlan.calendaris });
    void qc.invalidateQueries({ queryKey: ['torns'] });
    void qc.invalidateQueries({ queryKey: ['guardies'] });
  };
}

export interface TornDesat {
  data: TornDto;
  etag: string | null;
}

/**
 * Desa un torn. En modificar-lo s'envia `If-Match` amb l'ETag de la lectura amb què l'usuari
 * ha treballat (no se'n demana un de nou just abans del PUT: amagaria canvis concurrents i el
 * servidor no podria respondre 412).
 */
export async function desaTorn(
  torn: TornDto,
  opcions: { notificar?: boolean; etag?: string | null } = {},
): Promise<TornDesat> {
  if (torn.id == null) {
    return apiRequestAmbEtag<TornDto>('/torns', { method: 'POST', json: torn });
  }
  const { notificar = true, etag } = opcions;
  return apiRequestAmbEtag<TornDto>(`/torns/${torn.id}${query({ notificar })}`, {
    method: 'PUT',
    json: torn,
    headers: etag ? { 'If-Match': etag } : undefined,
  });
}

/** Torns d'un període amb l'ETag de cadascun, llegits en el mateix moment que la graella. */
export interface TornsAmbEtag {
  torns: TornDto[];
  etags: Record<number, string | null>;
}

export function useTornsAmbEtag(id: number, desde: string, fins: string) {
  return useQuery({
    queryKey: [...clausPlan.torns(id, desde, fins), 'etag'],
    queryFn: async (): Promise<TornsAmbEtag> => {
      const torns = await apiRequest<TornDto[]>(`/calendaris/${id}/torns${query({ desde, fins })}`);
      const etags: Record<number, string | null> = {};
      await Promise.all(
        torns
          .filter((t) => t.id != null)
          .map(async (t) => {
            const r = await apiRequestAmbEtag<TornDto>(`/torns/${t.id}`).catch(() => null);
            etags[t.id as number] = r?.etag ?? null;
          }),
      );
      return { torns, etags };
    },
    enabled: Number.isFinite(id),
  });
}

export function useDesaTorn() {
  const invalida = useInvalidaCalendari();
  return useMutation({
    mutationFn: ({
      torn,
      notificar,
      etag,
    }: {
      torn: TornDto;
      notificar?: boolean;
      etag?: string | null;
    }) => desaTorn(torn, { notificar, etag }),
    onSuccess: invalida,
  });
}

export function useInvalidaTorns() {
  return useInvalidaCalendari();
}

export function useEliminaTorn() {
  const invalida = useInvalidaCalendari();
  return useMutation({
    mutationFn: (id: number) =>
      apiRequest<void>(`/torns/${id}${query({ notificar: true })}`, { method: 'DELETE' }),
    onSuccess: invalida,
  });
}

export function useGeneraRotacio() {
  const invalida = useInvalidaCalendari();
  return useMutation({
    mutationFn: ({ rotacio, previsualitzar }: { rotacio: RotacioDto; previsualitzar: boolean }) =>
      apiRequest<ResultatRotacioDto>(`/rotacions${query({ previsualitzar })}`, {
        method: 'POST',
        json: rotacio,
      }),
    onSuccess: (_r, v) => {
      if (!v.previsualitzar) invalida();
    },
  });
}

export function useCreaCalendari() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (c: Omit<CalendariDto, 'id'>) =>
      apiRequest<CalendariDto>('/calendaris', { method: 'POST', json: c }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: clausPlan.calendaris }),
  });
}

export function useDesaExperts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serveiId, experts }: { serveiId: number; experts: ExpertServeiDto[] }) =>
      apiRequest<ExpertServeiDto[]>(`/serveis/${serveiId}/experts`, {
        method: 'PUT',
        json: experts,
      }),
    onSuccess: (_r, v) => void qc.invalidateQueries({ queryKey: ['serveis', v.serveiId] }),
  });
}

export function useResolSollicitud() {
  const qc = useQueryClient();
  const invalida = useInvalidaCalendari();
  return useMutation({
    mutationFn: ({
      id,
      accio,
      comentari,
    }: {
      id: number;
      accio: 'aprovar' | 'rebutjar';
      comentari?: string;
    }) =>
      apiRequest<SollicitudCanviDto>(`/sollicituds-canvi/${id}/${accio}`, {
        method: 'POST',
        json: { comentari: comentari || null },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clausPlan.sollicituds });
      invalida();
    },
  });
}
